/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Building Floor plan Unit
 */

import { ListingStatus } from '@zcode/enums'
import { dollarize } from '@zcode/utils'
import { format, isBefore } from 'date-fns'
import { stringToLocalDate } from '../utils/stringToLocalDate'
import { Bizo, autoclass } from './base'
import type { RawUnit, Unit as UnitType } from './unit.types'

export const Unit: typeof UnitType = autoclass<UnitType, RawUnit>(
  'Unit',
  class Unit extends Bizo<UnitType, RawUnit> {},
  {
    amenityTags: 'amenity_tags',
    buildingId: 'building_id',
    createdOn: 'created_on',
    dateAvailable: 'date_available',
    dateAvailableDate: (t: UnitType) =>
      t.dateAvailable ? stringToLocalDate(t.dateAvailable) : undefined,
    description: 'description',
    deposits: 'deposits',
    effectiveRent: 'effective_rent',
    feedName: 'feed_name',
    floor: 'floor',
    listingId: 'listing_id',
    listedOn: 'listed_on',
    maxPrice: 'max_price',
    media: 'media',
    modifiedOn: 'modified_on',
    minPrice: 'min_price',
    name: 'name',
    priceChange: 'price_change',
    providerId: 'provider_id',
    squareFeet: 'square_feet',
    statusText: 'status_text',
    unitId: 'unit_id',
    unitStatus: 'unit_status',
    vacancy: 'vacancy',
    dateAvailableText: ({ dateAvailableDate }: UnitType) => {
      if (!dateAvailableDate) {
        return ''
      }
      return format(dateAvailableDate, 'MMM d, yyyy') as UnitType['dateAvailableText']
    },
    dateAvailableTxt: ({ dateAvailableDate }: UnitType) => {
      if (!dateAvailableDate) {
        return ''
      }
      return format(dateAvailableDate, 'MM/dd') as UnitType['dateAvailableTxt']
    },
    dateAvailablePassed:
      ({ dateAvailableDate }: UnitType) =>
      (now: number) => {
        if (!dateAvailableDate) {
          return false
        }
        return isBefore(dateAvailableDate, new Date(now))
      },
    isActive: (t: UnitType) =>
      t.unitStatus === ListingStatus.ACTIVE || t.unitStatus === ListingStatus.DUPLICATE,
    priceText: (t: UnitType) => dollarize(t.minPrice || t.maxPrice) as UnitType['priceText'],
  }
)

export type Unit = UnitType
